import React, { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Users2, Plus, Search, Trash2, AlertCircle, Save, X, FileText, Mail, Phone,
  MapPin, Hash, Loader2, RefreshCw, Receipt,
} from "lucide-react";

const fmtEur = (v) =>
  new Intl.NumberFormat("ca-ES", { style: "currency", currency: "EUR" })
    .format(Number(v) || 0);

const blank = () => ({
  name: "", nif: "", address: "", city: "", postal_code: "", country: "ES",
  phone: "", email: "", website: "", iban: "", payment_terms: "", notes: "",
  is_active: true,
});

export default function Suppliers() {
  const { lang } = useI18n();
  const [list, setList] = useState([]);
  const [overview, setOverview] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/suppliers", { params: { q: search, only_active: true } }),
        api.get("/suppliers/stats/overview"),
      ]);
      setList(r1.data.items || []);
      setOverview(r2.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const openNew = () => { setEditing(null); setForm(blank()); setShowDialog(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "", nif: s.nif || "",
      address: s.address || "", city: s.city || "",
      postal_code: s.postal_code || "", country: s.country || "ES",
      phone: s.phone || "", email: s.email || "",
      website: s.website || "", iban: s.iban || "",
      payment_terms: s.payment_terms || "", notes: s.notes || "",
      is_active: s.is_active !== false,
    });
    setShowDialog(true);
  };
  const closeDialog = () => { setShowDialog(false); setEditing(null); setForm(blank()); };

  const save = async () => {
    if (!form.name.trim() && !form.nif.trim()) {
      toast.error(lang === "es" ? "Nombre o NIF requerido" : "Nom o NIF obligatori");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/suppliers/${editing.id}`, form);
      } else {
        await api.post("/suppliers", form);
      }
      toast.success(lang === "es" ? "Guardado" : "Desat");
      closeDialog();
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "duplicate_nif") {
        toast.error(lang === "es"
          ? `Ya existe un proveedor con NIF "${form.nif}"`
          : `Ja existeix un proveïdor amb NIF "${form.nif}"`);
      } else {
        toast.error(d || "Error");
      }
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm(
      lang === "es" ? `Eliminar "${editing.name}"?` : `Eliminar "${editing.name}"?`
    )) return;
    try {
      await api.delete(`/suppliers/${editing.id}`);
      toast.success(lang === "es" ? "Eliminado" : "Eliminat");
      closeDialog();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const backfill = async () => {
    if (!window.confirm(
      lang === "es"
        ? "Escanear todas las facturas existentes para crear proveedores y mercancías. Puede tardar unos segundos. ¿Continuar?"
        : "Escanejar totes les factures existents per crear proveïdors i mercaderies. Pot trigar uns segons. Continuar?"
    )) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/suppliers/backfill");
      toast.success(
        lang === "es"
          ? `${data.suppliers_processed} proveedores · ${data.products_materialized.created} mercancías nuevas · ${data.products_materialized.movements} movimientos`
          : `${data.suppliers_processed} proveïdors · ${data.products_materialized.created} mercaderies noves · ${data.products_materialized.movements} moviments`,
        { duration: 8000 }
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBackfilling(false); }
  };

  return (
    <div className="space-y-5" data-testid="suppliers-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">
            {lang === "es" ? "Proveedores" : "Proveïdors"}
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            {lang === "es"
              ? "Catálogo de proveedores con auto-detección al escanear facturas"
              : "Catàleg de proveïdors amb auto-detecció en escanejar factures"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={backfill}
            disabled={backfilling}
            className="border-white/10 text-slate-300 hover:bg-white/5"
            data-testid="suppliers-backfill-btn"
          >
            {backfilling
              ? <Loader2 size={14} className="mr-2 animate-spin" />
              : <RefreshCw size={14} className="mr-2" />}
            {lang === "es" ? "Importar de facturas" : "Importar de factures"}
          </Button>
          <Button
            onClick={openNew}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="suppliers-new-btn"
          >
            <Plus size={16} className="mr-2" />
            {lang === "es" ? "Nuevo" : "Nou"}
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={<Users2 size={16} />} label={lang === "es" ? "Proveedores" : "Proveïdors"} value={overview.total_suppliers || 0} tint="from-blue-500/15" />
        <Kpi icon={<Receipt size={16} />} label={lang === "es" ? "Facturas" : "Factures"} value={overview.invoices_count || 0} tint="from-emerald-500/15" />
        <Kpi icon={<FileText size={16} />} label={lang === "es" ? "Total gastado" : "Total gastat"} value={fmtEur(overview.total_spent || 0)} tint="from-violet-500/15" />
        <Kpi icon={<AlertCircle size={16} />} label={lang === "es" ? "Sin datos completos" : "Sense dades completes"} value={overview.missing_data_count || 0} tint="from-amber-500/15" />
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-3 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={lang === "es" ? "Buscar por nombre o NIF..." : "Cerca per nom o NIF..."}
          className="pl-9 bg-white/5 border-white/10"
          data-testid="suppliers-search-input"
        />
      </div>

      {/* Table */}
      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">{lang === "es" ? "Cargando..." : "Carregant..."}</div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Users2 size={32} className="mx-auto mb-3 text-slate-600" />
            <p>{lang === "es" ? "Sin proveedores. Importa desde facturas existentes o crea uno nuevo." : "Sense proveïdors. Importa des de factures existents o crea'n un de nou."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] border-b border-white/5">
                <tr className="text-xs uppercase tracking-wider text-slate-400">
                  <th className="text-left p-3">{lang === "es" ? "Nombre fiscal" : "Nom fiscal"}</th>
                  <th className="text-left p-3">NIF</th>
                  <th className="text-left p-3 hidden md:table-cell">{lang === "es" ? "Contacto" : "Contacte"}</th>
                  <th className="text-right p-3">{lang === "es" ? "Facturas" : "Factures"}</th>
                  <th className="text-right p-3 hidden sm:table-cell">{lang === "es" ? "Total" : "Total"}</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((s) => {
                  const incomplete = !s.email || !s.phone || !s.address;
                  return (
                    <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                        onClick={() => openEdit(s)}
                        data-testid={`supplier-row-${s.id}`}>
                      <td className="p-3">
                        <div className="text-slate-200 font-medium flex items-center gap-2">
                          {s.name || "—"}
                          {incomplete && (
                            <span title={lang === "es" ? "Datos incompletos" : "Dades incompletes"}>
                              <AlertCircle size={12} className="text-amber-400" />
                            </span>
                          )}
                        </div>
                        {s.address && <div className="text-xs text-slate-500 truncate max-w-[280px]">{s.address}</div>}
                      </td>
                      <td className="p-3 text-slate-300 font-mono text-xs">{s.nif || "—"}</td>
                      <td className="p-3 hidden md:table-cell">
                        <div className="text-xs space-y-0.5">
                          {s.email && <div className="text-slate-400 truncate max-w-[200px]">{s.email}</div>}
                          {s.phone && <div className="text-slate-500 font-mono">{s.phone}</div>}
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono text-slate-300">{s.invoices_count || 0}</td>
                      <td className="p-3 text-right font-mono text-white hidden sm:table-cell">{fmtEur(s.total_spent || 0)}</td>
                      <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => openEdit(s)} className="px-2 py-1 text-xs text-slate-400 hover:text-white rounded hover:bg-white/5">
                          {lang === "es" ? "Editar" : "Edita"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showDialog && (
        <EditOverlay
          lang={lang}
          editing={editing}
          form={form}
          setForm={setForm}
          saving={saving}
          onClose={closeDialog}
          onSave={save}
          onDelete={remove}
        />
      )}
    </div>
  );
}

function Kpi({ icon, label, value, tint = "" }) {
  return (
    <div className={`card-surface p-4 bg-gradient-to-br ${tint} to-transparent`}>
      <div className="flex items-start justify-between">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
        <div className="text-slate-300">{icon}</div>
      </div>
      <div className="text-xl font-bold text-white mt-1 font-mono truncate">{value}</div>
    </div>
  );
}

function EditOverlay({ lang, editing, form, setForm, saving, onClose, onSave, onDelete }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-150" onClick={onClose}>
      <div className="w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto bg-[#0B1220] border-t sm:border border-white/10 sm:rounded-2xl shadow-2xl"
           onClick={(e) => e.stopPropagation()}
           data-testid="supplier-edit-dialog">
        <div className="sticky top-0 z-10 bg-[#0B1220] border-b border-white/10 p-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-white">
            {editing ? (lang === "es" ? "Editar proveedor" : "Edita proveïdor") : (lang === "es" ? "Nuevo proveedor" : "Nou proveïdor")}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5 text-slate-400"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={lang === "es" ? "Nombre fiscal" : "Nom fiscal"} required>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-white/5 border-white/10" data-testid="supplier-name" />
            </Field>
            <Field label="NIF / CIF" icon={<Hash size={12} />}>
              <Input value={form.nif} onChange={(e) => set("nif", e.target.value.toUpperCase())} className="bg-white/5 border-white/10 font-mono" data-testid="supplier-nif" />
            </Field>
          </div>
          <Field label={lang === "es" ? "Dirección" : "Adreça"} icon={<MapPin size={12} />}>
            <Input value={form.address} onChange={(e) => set("address", e.target.value)} className="bg-white/5 border-white/10" data-testid="supplier-address" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label={lang === "es" ? "C.P." : "C.P."}>
              <Input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} className="bg-white/5 border-white/10 font-mono" />
            </Field>
            <Field label={lang === "es" ? "Ciudad" : "Ciutat"}>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-white/5 border-white/10" />
            </Field>
            <Field label={lang === "es" ? "País" : "País"}>
              <Input value={form.country} onChange={(e) => set("country", e.target.value)} className="bg-white/5 border-white/10 font-mono" />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={lang === "es" ? "Email" : "Email"} icon={<Mail size={12} />}>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-white/5 border-white/10" data-testid="supplier-email" />
            </Field>
            <Field label={lang === "es" ? "Teléfono" : "Telèfon"} icon={<Phone size={12} />}>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-white/5 border-white/10 font-mono" data-testid="supplier-phone" />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Web">
              <Input value={form.website} onChange={(e) => set("website", e.target.value)} className="bg-white/5 border-white/10" />
            </Field>
            <Field label="IBAN">
              <Input value={form.iban} onChange={(e) => set("iban", e.target.value)} className="bg-white/5 border-white/10 font-mono" />
            </Field>
          </div>
          <Field label={lang === "es" ? "Condiciones de pago" : "Condicions de pagament"}>
            <Input value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} className="bg-white/5 border-white/10" placeholder={lang === "es" ? "p.ej. 30 días" : "p.ex. 30 dies"} />
          </Field>
          <Field label={lang === "es" ? "Notas" : "Notes"}>
            <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-white/5 border-white/10" />
          </Field>
        </div>
        <div className="sticky bottom-0 bg-[#0B1220] border-t border-white/10 p-4 flex items-center justify-between gap-2">
          {editing ? (
            <Button variant="ghost" onClick={onDelete} className="text-rose-400 hover:bg-rose-500/10">
              <Trash2 size={14} className="mr-1.5" />
              {lang === "es" ? "Eliminar" : "Elimina"}
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              {lang === "es" ? "Cancelar" : "Cancel·la"}
            </Button>
            <Button onClick={onSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="supplier-save-btn">
              <Save size={14} className="mr-1.5" />
              {lang === "es" ? "Guardar" : "Desa"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, icon, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
        {icon}{label}{required && <span className="text-rose-400">*</span>}
      </label>
      {children}
    </div>
  );
}
